/**
 * CRM ▸ **Releases** (`/crm/releases`) — a folder per release, and nothing
 * else.
 *
 * XOP ships three or four releases at a time (9.3, 10.2, 11) and dozens of
 * versions inside them. This page is the top level only: the folders, newest
 * first by the newest version inside each. Opening one NAVIGATES to that
 * release's own page (`/crm/releases/10.2`, `ReleaseDetailPage`) — a release
 * is a place with an address, not a panel swapped in here.
 *
 * It was the Releases tab of CRM ▸ Releases & Licences until 2026-09-13, when
 * the two became sidebar entries of their own. The grant is unchanged —
 * `crm:products_pricing` (→ `products_pricing:access`), the same one that
 * opens the catalogue and the price book, because releases, products and
 * prices are one body of product knowledge. Visibility is all it confers:
 * curating a release needs `admin:users`, taking a disk image needs
 * `releases:download_images`, and portal-api decides and records either way.
 */
import { Box, Button, Card, CardContent, Chip, Typography } from "@mui/material";
import { Plus } from "lucide-react";
import type { JSX } from "react";
import { useNavigate } from "react-router-dom";

import { PageHeader } from "@/components/ui/page-header";
import { PageLoader } from "@/components/ui/page-loader";
import { PagePermissionGuard } from "@crm-src/components/page-permission-guard";

import { Pagination } from "../../../components/ui/pagination.js";
import { usePagedList } from "../../../hooks/usePagedList.js";
import { ApiError } from "../../../lib/api.js";
import { ReleaseFolderCard } from "./ReleaseFolderCard.js";
import { releasePath } from "./release-paths.js";
import { statusLabel } from "./release-status.js";
import { useReleaseCuration } from "./useReleaseCuration.js";
import { useReleases } from "./useReleases.js";

export const RELEASES_TITLE = "Software Releases";

export default function ReleasesPage(): JSX.Element {
  return (
    <PagePermissionGuard requiredPermission="products_pricing:access">
      <ReleaseFolders />
    </PagePermissionGuard>
  );
}

function ReleaseFolders(): JSX.Element {
  const navigate = useNavigate();
  const { families, latest, versionCount, isLoading, isError, error, refetch, invalidate } =
    useReleases();
  const curation = useReleaseCuration({ families, invalidate });
  const { paged, page, perPage, totalCount, totalPages, setPage, setPerPage } =
    usePagedList(families, 25);

  return (
    <Box>
      <PageHeader
        title={RELEASES_TITLE}
        actions={
          curation.canEdit ? (
            <Button
              variant="contained"
              onClick={curation.openCreate}
              aria-label="New release"
              startIcon={<Plus size={16} />}
            >
              New release
            </Button>
          ) : undefined
        }
      />

      <Card sx={{ mt: 3 }}>
        {families.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
              borderBottom: 1,
              borderColor: "divider",
              px: 2.5,
              py: 1.5,
            }}
          >
            <Chip
              size="small"
              variant="outlined"
              label={`${families.length} release${families.length === 1 ? "" : "s"} · ${versionCount} version${versionCount === 1 ? "" : "s"}`}
              sx={{ height: 22 }}
            />
            {latest && (
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={`Latest ${latest.version} · ${statusLabel(latest.status)}`}
                sx={{ height: 22 }}
              />
            )}
          </Box>
        )}
        <CardContent sx={{ px: 2.5, py: 2 }}>
          {isLoading ? (
            <PageLoader />
          ) : isError ? (
            <Box sx={{ borderRadius: 1, border: 1, borderColor: "error.main", p: 2, fontSize: 14 }}>
              <Typography sx={{ mb: 1, color: "error.main" }}>
                Could not load releases
                {error instanceof ApiError && error.message ? `: ${error.message}` : "."}
              </Typography>
              <Button variant="contained" size="small" onClick={() => void refetch()}>
                Retry
              </Button>
            </Box>
          ) : families.length === 0 ? (
            <Typography sx={{ py: 3, textAlign: "center", fontSize: 14, color: "text.disabled" }}>
              No releases yet.
              {curation.canEdit ? " Use “New release” to add the first one." : ""}
            </Typography>
          ) : (
            <>
              <Box
                data-testid="release-folders"
                sx={{
                  display: "grid",
                  gap: 1.5,
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    lg: "repeat(3, minmax(0, 1fr))",
                  },
                }}
              >
                {paged.map((family) => (
                  <ReleaseFolderCard
                    key={family.line.id}
                    family={family}
                    canEdit={curation.canEdit}
                    onOpen={() => navigate(releasePath(family.line.version))}
                    onEdit={() => curation.openEdit(family.line)}
                    onDelete={() => curation.requestDelete(family.line)}
                  />
                ))}
              </Box>
              <Box sx={{ mt: 1.5 }}>
                <Pagination
                  page={page}
                  perPage={perPage}
                  totalCount={totalCount}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  onPerPageChange={setPerPage}
                  entityNoun="releases"
                  hideIfSinglePage
                />
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {curation.dialogs}
    </Box>
  );
}
